import React, { useState } from "react";

import {
  Card,
  CardActions,
  CardHeader,
  CardContent,
  makeStyles,
  Grid,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import { FileRejection } from "react-dropzone";

import DragAndDrop from "./DragAndDrop";
import useSaveBAA from "../mutations/useSaveBAA";

import SelectedFileDetails from "./SelectedFileDetails";

import { WebTrackerResolution } from "../types";
import useAddWebTrackerResolution from "../mutations/useAddWebTrackerResolution";

const useStyles = makeStyles((theme) => ({
  uploadArea: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  uploaderActions: {
    alignContent: "center",
    justifyContent: "right",
    marginTop: theme.spacing(1),
  },
  card: {
    position: "relative",
    minWidth: 500,
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  fileDetailsContainer: {
    alignContent: "center",
    justifyContent: "right",
    marginTop: theme.spacing(1),
  },
  cancelBtn: {
    marginRight: theme.spacing(2),
  },
  subTitle: {
    marginTop: theme.spacing(1.8),
  },
}));

export type BAAUploaderProps = {
  onClose: () => void;
  siteDomain: string;
  trackerName: string;
  trackerDomain: string;
  resolution: WebTrackerResolution;
  setIsResolutionLoading: (value: React.SetStateAction<boolean>) => void;
};

function BAAUploader({
  onClose,
  siteDomain,
  trackerDomain,
  trackerName,
  resolution,
  setIsResolutionLoading,
}: BAAUploaderProps) {
  const classes = useStyles();
  const alert = useAlert();

  const [acceptedFile, setAcceptedFile] = useState<File | null>(null);
  const [rejectedFile, setRejectedFile] = useState<FileRejection | null>(null);

  const handleDropAccepted = (acceptedFile: File) => {
    setRejectedFile(null);
    setAcceptedFile(acceptedFile);
  };

  const handleDropRejected = (rejectedFile: FileRejection) => {
    setAcceptedFile(null);
    setRejectedFile(rejectedFile);
  };

  const onSaveBAASuccess = () => {
    onClose();
    alert.success("Upload succeeded");

    if (resolution?.resolutionType !== "signed_baa") {
      setIsResolutionLoading(true);
      addWebTrackerResolutionMutation.mutate({
        note: resolution?.note || "",
        trackerDomain,
        trackerName,
        oldResolutionType: resolution?.resolutionType ?? "unresolved",
        newResolutionType: "signed_baa",
      });
    }
  };

  const onSaveBAAError = () => {
    onClose();
    alert.error("Upload failed");
  };

  const saveBAAMutation = useSaveBAA(onSaveBAASuccess, onSaveBAAError);
  const isBAAUploadInProgress = saveBAAMutation.isLoading;

  const onAddResolutionSuccess = () => alert.success("Resolution updated");
  const onAddResolutionSettled = () => setIsResolutionLoading(false);
  const addWebTrackerResolutionMutation = useAddWebTrackerResolution(
    onAddResolutionSuccess,
    onAddResolutionSettled
  );

  const handleSubmitUpload = () => {
    if (!acceptedFile) {
      return;
    }
    saveBAAMutation.mutate({
      file: acceptedFile,
      siteDomain,
      trackerDomain,
      trackerName,
    });
  };

  return (
    <Card className={classes.card}>
      {isBAAUploadInProgress && (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}
      <CardHeader
        disableTypography
        title={
          <Typography component="h5" variant="h5">
            Upload your BAA
          </Typography>
        }
        subheader={
          <Typography
            className={classes.subTitle}
            component="p"
            variant="subtitle2"
          >
            {trackerName} will be marked as resolved with "Signed BAA" status
            once your BAA is uploaded.
          </Typography>
        }
      />
      <CardContent className={classes.uploadArea}>
        <DragAndDrop
          onDropAcceptedHandler={handleDropAccepted}
          onDropRejectedHandler={handleDropRejected}
          maxFileSize={25000000}
        />
        <Grid container className={classes.fileDetailsContainer}>
          <Grid item>
            <SelectedFileDetails
              acceptedFile={acceptedFile}
              rejectedFile={rejectedFile}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container className={classes.uploaderActions}>
          <Grid item>
            <Button
              className={classes.cancelBtn}
              variant="text"
              color="secondary"
              onClick={onClose}
              size="small"
              title="Cancel Uploading Document"
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              disabled={!acceptedFile}
              variant="contained"
              color="primary"
              onClick={handleSubmitUpload}
              size="small"
              title="Upload Document"
            >
              Upload Document
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
}

export default BAAUploader;
